/**
 * 推广卡管理 — GET/POST/PATCH /api/admin/pcc/cards + POST .../settle
 *
 * Admin CRUD for the PCC (Passive Compensation Card) catalog. Cards are
 * limited-issue: `total_count` is frozen at creation, `code` is an immutable
 * slug, and the card is archived (never hard-deleted) by flipping
 * `is_active=false` or by running a settlement with `mark_settled=true`.
 *
 * A settled card rejects further purchases and further settlement runs —
 * that's why the settle modal is a one-way action gated on the
 * `pcc.card.settle` node.
 */
import { useState } from 'react';
import {
  Button,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Switch,
  Tag,
  message,
} from 'antd';
import { useQueryClient } from '@tanstack/react-query';
import { DataTable } from '../../components/DataTable';
import { api, ApiError } from '../../api/client';
import { useAuthStore } from '../../store/auth';

interface CardRow {
  id: number;
  code: string;
  title_zh: string;
  title_en: string;
  description_zh: string;
  description_en: string;
  image_url: string | null;
  price: string;
  pcc_rate: string;
  total_count: number;
  residue_count: number;
  quota: number;
  start_time: string | null;
  end_time: string | null;
  is_active: boolean;
  is_settled: boolean;
  sort: number;
  create_time: string;
  update_time: string;
  sold_pct: number;
  holder_count: number;
}

interface CardCreateForm {
  code: string;
  title_zh: string;
  title_en: string;
  description_zh: string;
  description_en: string;
  image_url?: string;
  price: string;
  pcc_rate: string;
  total_count: number;
  quota: number;
  sort: number;
  is_active: boolean;
}

type CardPatchForm = Omit<CardCreateForm, 'code' | 'total_count'>;

interface SettleForm {
  per_holding_earnings: string;
  mark_settled: boolean;
  note?: string;
}

export function PccCardsPage() {
  const qc = useQueryClient();
  const [isActiveFilter, setIsActiveFilter] = useState<string | undefined>(undefined);
  const [isSettledFilter, setIsSettledFilter] = useState<string | undefined>(undefined);
  const [createOpen, setCreateOpen] = useState(false);
  const [editRow, setEditRow] = useState<CardRow | null>(null);
  const [settleRow, setSettleRow] = useState<CardRow | null>(null);
  const [createForm] = Form.useForm<CardCreateForm>();
  const [editForm] = Form.useForm<CardPatchForm>();
  const [settleForm] = Form.useForm<SettleForm>();
  const hasNode = useAuthStore((s) => s.hasNode);
  const canCreate = hasNode('pcc.card.create');
  const canEdit = hasNode('pcc.card.update');
  const canSettle = hasNode('pcc.card.settle');

  function invalidate() {
    qc.invalidateQueries({ queryKey: ['admin', 'pcc-cards'] });
  }

  async function onCreate(values: CardCreateForm) {
    try {
      await api.post('/api/admin/pcc/cards', values);
      message.success('推广卡已创建');
      setCreateOpen(false);
      createForm.resetFields();
      invalidate();
    } catch (err) {
      message.error(err instanceof ApiError ? err.message : '创建失败');
    }
  }

  async function onEdit(values: CardPatchForm) {
    if (!editRow) return;
    try {
      await api.patch(`/api/admin/pcc/cards/${editRow.id}`, values);
      message.success('已更新');
      setEditRow(null);
      invalidate();
    } catch (err) {
      message.error(err instanceof ApiError ? err.message : '更新失败');
    }
  }

  async function onSettle(values: SettleForm) {
    if (!settleRow) return;
    try {
      const res = await api.post<{
        card_id: number;
        card_code: string;
        holdings_paid: number;
        total_paid_ep: string;
        marked_settled: boolean;
      }>(`/api/admin/pcc/cards/${settleRow.id}/settle`, values);
      message.success(
        `结算完成：${res.holdings_paid} 份持仓，累计发放 ${res.total_paid_ep} EP${
          res.marked_settled ? '（已归档）' : ''
        }`,
      );
      setSettleRow(null);
      settleForm.resetFields();
      invalidate();
    } catch (err) {
      message.error(err instanceof ApiError ? err.message : '结算失败');
    }
  }

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 70 },
    {
      title: '封面',
      dataIndex: 'image_url',
      width: 100,
      render: (v: string | null) =>
        v ? (
          <img src={v} alt="" style={{ height: 40, maxWidth: 80, objectFit: 'cover' }} />
        ) : (
          '—'
        ),
    },
    { title: 'Code', dataIndex: 'code', width: 140 },
    { title: '中文标题', dataIndex: 'title_zh', ellipsis: true, width: 180 },
    { title: '英文标题', dataIndex: 'title_en', ellipsis: true, width: 180 },
    { title: '单价 (ORB)', dataIndex: 'price', width: 120 },
    {
      title: '收益率',
      dataIndex: 'pcc_rate',
      width: 100,
      render: (v: string) => `${(Number(v) * 100).toFixed(2)}%`,
    },
    {
      title: '供给',
      key: 'supply',
      width: 140,
      render: (_: unknown, row: CardRow) =>
        row.total_count === 0
          ? '不限量'
          : `${row.total_count - row.residue_count}/${row.total_count}`,
    },
    {
      title: '售罄率',
      dataIndex: 'sold_pct',
      width: 100,
      render: (v: number) => `${v.toFixed(2)}%`,
    },
    {
      title: '限购',
      dataIndex: 'quota',
      width: 80,
      render: (v: number) => (v === 0 ? '不限' : v),
    },
    { title: '持有人数', dataIndex: 'holder_count', width: 100 },
    { title: '排序', dataIndex: 'sort', width: 70 },
    {
      title: '状态',
      key: 'status',
      width: 140,
      render: (_: unknown, row: CardRow) => {
        if (row.is_settled) return <Tag color="blue">已结算</Tag>;
        if (row.is_active) return <Tag color="green">在售</Tag>;
        return <Tag>已下架</Tag>;
      },
    },
    { title: '创建时间', dataIndex: 'create_time', width: 170 },
    {
      title: '操作',
      key: 'op',
      width: 180,
      fixed: 'right' as const,
      render: (_: unknown, row: CardRow) => {
        const buttons: JSX.Element[] = [];
        if (canEdit && !row.is_settled) {
          buttons.push(
            <Button
              key="edit"
              type="link"
              size="small"
              onClick={() => {
                setEditRow(row);
                editForm.setFieldsValue({
                  title_zh: row.title_zh,
                  title_en: row.title_en,
                  description_zh: row.description_zh,
                  description_en: row.description_en,
                  image_url: row.image_url ?? undefined,
                  price: row.price,
                  pcc_rate: row.pcc_rate,
                  quota: row.quota,
                  sort: row.sort,
                  is_active: row.is_active,
                });
              }}
            >
              编辑
            </Button>,
          );
        }
        if (canSettle && !row.is_settled) {
          buttons.push(
            <Button
              key="settle"
              type="link"
              size="small"
              onClick={() => {
                setSettleRow(row);
                settleForm.setFieldsValue({
                  per_holding_earnings: '0',
                  mark_settled: false,
                  note: undefined,
                });
              }}
            >
              结算
            </Button>,
          );
        }
        return buttons.length > 0 ? <Space size={0}>{buttons}</Space> : '—';
      },
    },
  ];

  return (
    <div>
      <h2 style={{ marginTop: 0 }}>推广卡</h2>
      <Space style={{ marginBottom: 12 }} wrap>
        <Select
          placeholder="上架状态"
          style={{ width: 140 }}
          allowClear
          value={isActiveFilter}
          onChange={setIsActiveFilter}
          options={[
            { value: 'true', label: '在售' },
            { value: 'false', label: '已下架' },
          ]}
        />
        <Select
          placeholder="结算状态"
          style={{ width: 140 }}
          allowClear
          value={isSettledFilter}
          onChange={setIsSettledFilter}
          options={[
            { value: 'false', label: '未结算' },
            { value: 'true', label: '已结算' },
          ]}
        />
        {canCreate && (
          <Button type="primary" onClick={() => setCreateOpen(true)}>
            新建推广卡
          </Button>
        )}
      </Space>

      <DataTable<CardRow>
        queryKey={['admin', 'pcc-cards', isActiveFilter ?? '', isSettledFilter ?? '']}
        endpoint="/api/admin/pcc/cards"
        extraParams={{ is_active: isActiveFilter, is_settled: isSettledFilter }}
        columns={columns}
        rowKey="id"
        scroll={{ x: 2100 }}
      />

      <Modal
        open={createOpen}
        title="新建推广卡"
        onCancel={() => setCreateOpen(false)}
        onOk={() => createForm.submit()}
        width={720}
        destroyOnClose
      >
        <Form
          form={createForm}
          layout="vertical"
          initialValues={{
            price: '0',
            pcc_rate: '0',
            total_count: 0,
            quota: 0,
            sort: 0,
            is_active: true,
          }}
          onFinish={onCreate}
        >
          <Form.Item
            name="code"
            label="Code (slug)"
            tooltip="创建后不可修改；仅允许小写字母、数字、下划线"
            rules={[
              { required: true, max: 64 },
              { pattern: /^[a-z0-9_]+$/, message: '格式不合法' },
            ]}
          >
            <Input placeholder="例：booster_001" />
          </Form.Item>
          <Form.Item name="title_zh" label="中文标题" rules={[{ required: true, max: 200 }]}>
            <Input />
          </Form.Item>
          <Form.Item name="title_en" label="英文标题" rules={[{ required: true, max: 200 }]}>
            <Input />
          </Form.Item>
          <Form.Item name="description_zh" label="中文描述" rules={[{ required: true }]}>
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item name="description_en" label="英文描述" rules={[{ required: true }]}>
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item name="image_url" label="封面 URL">
            <Input />
          </Form.Item>
          <Form.Item name="price" label="单价 (ORB)" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item
            name="pcc_rate"
            label="收益率"
            tooltip="结算时对匹配成交量的乘数，如 0.05 = 5%"
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="total_count"
            label="总发行量"
            tooltip="创建后冻结，不可修改；0 = 不限量"
          >
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="quota" label="单用户限购" tooltip="0 = 不限购">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="sort" label="排序">
            <InputNumber />
          </Form.Item>
          <Form.Item name="is_active" label="上架" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={editRow !== null}
        title={`编辑推广卡 · ${editRow?.code ?? ''}`}
        onCancel={() => setEditRow(null)}
        onOk={() => editForm.submit()}
        width={720}
        destroyOnClose
      >
        <Form form={editForm} layout="vertical" onFinish={onEdit}>
          <Form.Item name="title_zh" label="中文标题" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="title_en" label="英文标题" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="description_zh" label="中文描述" rules={[{ required: true }]}>
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item name="description_en" label="英文描述" rules={[{ required: true }]}>
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item name="image_url" label="封面 URL">
            <Input />
          </Form.Item>
          <Form.Item name="price" label="单价 (ORB)">
            <Input />
          </Form.Item>
          <Form.Item name="pcc_rate" label="收益率">
            <Input />
          </Form.Item>
          <Form.Item name="quota" label="单用户限购">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="sort" label="排序">
            <InputNumber />
          </Form.Item>
          <Form.Item name="is_active" label="上架" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={settleRow !== null}
        title={`结算推广卡 · ${settleRow?.code ?? ''}`}
        onCancel={() => setSettleRow(null)}
        onOk={() => settleForm.submit()}
        okText="执行结算"
        okButtonProps={{ danger: true }}
        width={600}
        destroyOnClose
      >
        <p style={{ marginTop: 0, color: '#8c8c8c' }}>
          每份持仓将被发放相同数额的 EP，并通过 LedgerService 记录 pcc_earnings。勾选
          &ldquo;归档卡片&rdquo; 后将关闭后续购买与结算，不可撤销。
        </p>
        <Form form={settleForm} layout="vertical" onFinish={onSettle}>
          <Form.Item
            name="per_holding_earnings"
            label="每份持仓发放 EP"
            tooltip="flat amount，通常 = match_volume × pcc_rate（链下计算）"
            rules={[{ required: true }]}
          >
            <Input placeholder="例：12.50" />
          </Form.Item>
          <Form.Item
            name="mark_settled"
            label="归档卡片（mark_settled）"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
          <Form.Item name="note" label="备注">
            <Input.TextArea rows={2} maxLength={200} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
